import { Bucket } from './taskBuckets';

/**
 * Цвета доски задач — ОДИН источник на доску, список и Гант (ТЗ 0065).
 *
 * 🔴 Зачем отдельный файл, а не hex по месту. До него один и тот же признак был
 * покрашен по-разному в двух видах одного экрана:
 *
 *   - статус «в работе» — `#1e3a8a` на карточке доски и `#1d4ed8` в строке
 *     списка;
 *   - высокий приоритет — КРАСНЫЙ на карточке и ЯНТАРНЫЙ в списке.
 *
 * Это не вкусовщина: человек читает цвет как признак, и два цвета для одного
 * признака означают, что признаков как будто два. Файлов было два
 * (`TaskBoard.styled` и `TaskList.styled`), и правка одного не доезжала до
 * второго — расхождение появилось само и молча. Тест
 * `__tests__/taskTones.test.ts` следит, чтобы каждая колонка и каждый статус
 * имели тон и чтобы список не завёл свой.
 *
 * Литералы Tailwind (slate/blue/amber/violet/emerald) оставлены сознательно:
 * весь «красивый» слой ERP — Zoom, карьерный центр, дофинансирование — держит
 * ту же палитру так же. Перевод ОДНОГО модуля на `services/ColorService` завёл
 * бы третью систему рядом с двумя существующими, а не убрал бы одну.
 */

export interface ITaskTone {
  /** Текст и значок. */
  fg: string;
  /** Мягкая заливка под текст: чип, шапка колонки. */
  bg: string;
  /** Линия: подчёркивание шапки, рамка, заливка полосы Ганта. */
  line: string;
}

export const NEUTRAL_TONE: ITaskTone = {
  fg: '#475569',
  bg: '#f1f5f9',
  line: '#cbd5e1',
};

/**
 * Тон «без тона»: так шапка колонки выглядела до ТЗ 0065.
 *
 * 🔴 Нужен не доске задач, а её СОСЕДУ: воронка дофинансирования
 * (`Dofinansowanie/parts/board`) переиспользует `B.Column`/`B.ColumnHead`
 * доски и своих тонов не имеет. Сделать `$tone` обязательным значило бы
 * покрасить чужой экран заодно.
 */
export const PLAIN_TONE: ITaskTone = {
  fg: '#475569',
  bg: 'transparent',
  line: '#e2e8f0',
};

/** Ещё тише нейтрального: подписи групп «через неделю», «без срока» — они
 *  ориентируют, но не зовут. */
export const MUTED_TONE: ITaskTone = {
  fg: '#94a3b8',
  bg: '#f8fafc',
  line: '#e2e8f0',
};

/**
 * Три уровня глубины. До ТЗ 0065 их было полтора: белая рабочая область и
 * колонки `#f8fafc` — разница в один процент светлоты, и границы колонок
 * читались как единственный разделитель. Теперь фон серый, колонка светлее
 * фона, карточка белая.
 */
export const SURFACE = {
  /** Фон рабочей области — под колонками. */
  app: '#eef2f7',
  /** Колонка. */
  column: '#f8fafc',
  /** Карточка, строка списка, окно. */
  card: '#fff',
  line: '#e2e8f0',
  lineStrong: '#cbd5e1',
  text: '#0f172a',
  muted: '#64748b',
  mutedSoft: '#94a3b8',
  accent: '#2563eb',
  accentSoft: '#eff6ff',
  accentLine: '#93c5fd',
};

/**
 * Тона колонок по срокам. Раскрашены ВСЕ ПЯТЬ: раньше цвет был только у
 * «Просроченных» и «Сегодня», а три оставшиеся колонки не отличались друг от
 * друга ничем, кроме подписи, — и доска читалась как «две важные и куча
 * одинаковых».
 *
 * Порядок тонов повторяет срочность: красный → янтарный → синий → фиолетовый →
 * нейтральный.
 */
export const BUCKET_TONES: Record<Bucket, ITaskTone> = {
  overdue: { fg: '#b91c1c', bg: '#fef2f2', line: '#dc2626' },
  today: { fg: '#b45309', bg: '#fffbeb', line: '#d97706' },
  tomorrow: { fg: '#1d4ed8', bg: '#eff6ff', line: '#2563eb' },
  thisWeek: { fg: '#6d28d9', bg: '#f5f3ff', line: '#7c3aed' },
  later: NEUTRAL_TONE,
  noDate: NEUTRAL_TONE,
  done: { fg: '#047857', bg: '#d1fae5', line: '#059669' },
};

/**
 * Тона статусов. «Запланировано» намеренно СЕРО-СИНЕЕ, а не синее: синий здесь
 * уже занят работой, и два синих рядом не различаются.
 */
export const STATUS_TONES: Record<string, ITaskTone> = {
  open: { fg: '#475569', bg: '#f1f5f9', line: '#94a3b8' },
  in_progress: { fg: '#1d4ed8', bg: '#dbeafe', line: '#2563eb' },
  done: { fg: '#047857', bg: '#d1fae5', line: '#059669' },
  cancelled: { fg: '#64748b', bg: '#f1f5f9', line: '#cbd5e1' },
};

/**
 * Тон предупреждения — «есть несохранённые изменения».
 *
 * 🔴 Отдельный, а не «янтарный бакет Сегодня». Когда чип взял тон колонки,
 * фон побледнел с #fef3c7 до #fffbeb, и предупреждение стало почти
 * незаметным. Колонка и предупреждение решают разные задачи: у колонки фон —
 * подложка под заголовок, у чипа — единственный способ докричаться.
 */
export const ATTENTION_TONE: ITaskTone = {
  fg: '#92400e',
  bg: '#fef3c7',
  line: '#d97706',
};

/**
 * Тона приоритета. Низкий и обычный НЕ выделяются: подсветить обычное — значит
 * потерять высокое. Высокий красный — тот же красный, что у просрочки, и это
 * осознанно: оба означают «сюда смотреть первым».
 */
export const PRIORITY_TONES: Record<string, ITaskTone> = {
  high: { fg: '#b91c1c', bg: '#fee2e2', line: '#dc2626' },
  normal: NEUTRAL_TONE,
  low: NEUTRAL_TONE,
};

/**
 * Тон шапки колонки по её ключу. Колонки доски бывают двух группировок —
 * бакеты сроков и статусы, — и ключ однозначно называет одну из них. Ключ
 * `done` есть в обеих картах и в обеих зелёный, так что порядок поиска на него
 * не влияет.
 */
export const columnTone = (key: string): ITaskTone =>
  BUCKET_TONES[key as Bucket] || STATUS_TONES[key] || NEUTRAL_TONE;

/** Признаки, которые карточка доски и строка списка показывают чипом. */
export type TChipTone = 'warn' | 'accent' | 'high' | 'low';

/**
 * Тон чипа. ОДНА функция на оба вида — и в этом весь смысл.
 *
 * 🔴 Пока их было две (по одной в `TaskBoard.styled` и `TaskList.styled`), они
 * разъехались: «в работе» — `#1e3a8a` на доске и `#1d4ed8` в списке, высокий
 * приоритет — красный на доске и ЯНТАРНЫЙ в списке. Разъехались молча: правка
 * одного файла ничем не касалась второго. Теперь у них общее тело, и
 * разойтись им негде.
 */
export const chipTone = (tone?: TChipTone): ITaskTone => {
  if (tone === 'accent') return STATUS_TONES.in_progress;
  if (tone === 'warn') return ATTENTION_TONE;
  if (tone === 'high' || tone === 'low') return PRIORITY_TONES[tone];
  return NEUTRAL_TONE;
};
